import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Calendar, Info, Save, X } from 'lucide-react';

const EditPayrollModal = ({ show, handleClose, handleSave, initialDate, title, isUpdating = false, payrollPeriod = null }) => {
    const [selectedDate, setSelectedDate] = useState('');
    const [error, setError] = useState('');

    console.log('EditPayrollModal', initialDate, title, isUpdating, payrollPeriod);

    // Helper function to format date in local timezone for input[type="date"]
    const formatDateForInput = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        // Format the date when the modal opens
        if (initialDate) {
            const formattedDate = formatDateForInput(initialDate);
            setSelectedDate(formattedDate);
        } else {
            setSelectedDate(formatDateForInput(new Date()));
        }
        setError(''); // Clear error when modal opens
    }, [initialDate, show]);

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
        setError(''); // Clear error when user changes date
    };

    const handleSaveClick = async () => {
        if (!selectedDate) {
            setError('Vänligen välj ett utbetalningsdatum');
            return;
        }

        // Create date in local timezone to avoid UTC conversion issues
        const [year, month, day] = selectedDate.split('-').map(Number);
        const selectedDateObj = new Date(year, month - 1, day);

        // Always validate that the payout date is not in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDateObj < today) {
            setError('Utbetalningsdatum kan inte vara i det förflutna');
            return;
        }

        // Validate that the payout date falls within the payroll period
        if (payrollPeriod) {
            const periodStart = new Date(payrollPeriod.year, payrollPeriod.month - 1, 1);
            const periodEnd = new Date(payrollPeriod.year, payrollPeriod.month, 0);
            
            if (selectedDateObj < periodStart || selectedDateObj > periodEnd) {
                const monthName = new Date(payrollPeriod.year, payrollPeriod.month - 1).toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' });
                const periodStartFormatted = periodStart.toLocaleDateString('sv-SE');
                const periodEndFormatted = periodEnd.toLocaleDateString('sv-SE');
                
                setError(`Utbetalningsdatum måste vara inom löneperioden för ${monthName} (${periodStartFormatted} - ${periodEndFormatted})`);
                return;
            }
        }

        try {
            await handleSave(selectedDate);
            handleClose();
            setError('');
        } catch (err) {
            setError('Ett fel uppstod när lönekörningen skulle uppdateras');
        }
    };

    const handleModalClose = () => {
        if (!isUpdating) {
            setError('');
            handleClose();
        }
    };

    const getPayoutDateInfo = () => {
        if (!selectedDate) return '';

        const date = new Date(selectedDate);
        const dayOfWeek = date.toLocaleDateString('sv-SE', { weekday: 'long' });
        const formatted = date.toLocaleDateString('sv-SE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });

        return `${dayOfWeek}, ${formatted}`;
    };

    return (
        <>
            <style>{`
                .modern-modal .modal-content {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    overflow: hidden;
                }

                .modern-modal-header {
                    background: white;
                    color: #1e293b;
                    padding: 2rem;
                    border-bottom: 1px solid #e5e7eb;
                }

                .modern-modal-header .modal-title {
                    font-size: 1.5rem;
                    font-weight: 600;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .modern-modal-header .modal-title svg {
                    color: #3b82f6;
                }

                .modern-modal-header .btn-close {
                    background: none;
                    border: none;
                    color: #64748b;
                    opacity: 0.8;
                    font-size: 1.5rem;
                    cursor: pointer;
                    transition: opacity 0.2s ease;
                    padding: 0;
                    margin: 0;
                }

                .modern-modal-header .btn-close:hover {
                    opacity: 1;
                    color: #374151;
                }

                .modern-modal-body {
                    padding: 2rem;
                    background: #f8fafc;
                }

                .modal-info-section {
                    background: #eff6ff;
                    border: 1px solid #bfdbfe;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 2rem;
                    display: flex;
                    gap: 0.75rem;
                }

                .modal-info-icon {
                    color: #3b82f6;
                    flex-shrink: 0;
                }

                .modal-info-text {
                    color: #1e40af;
                    font-size: 0.875rem;
                    line-height: 1.5;
                }

                .modern-form-group {
                    margin-bottom: 1.5rem;
                }

                .modern-form-label {
                    display: block;
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                }

                .modern-form-input {
                    width: 100%;
                    padding: 0.75rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 6px;
                    font-size: 1rem;
                    transition: all 0.2s ease;
                    background: white;
                }

                .modern-form-input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .modern-form-input:invalid {
                    border-color: #dc2626;
                }

                .date-preview {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    padding: 1rem;
                    margin-top: 0.75rem;
                }

                .date-preview-label {
                    font-size: 0.75rem;
                    color: #6b7280;
                    margin-bottom: 0.25rem;
                    text-transform: uppercase;
                    font-weight: 500;
                    letter-spacing: 0.5px;
                }

                .date-preview-value {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1f2937;
                }

                .error-message {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    color: #dc2626;
                    padding: 0.75rem;
                    border-radius: 6px;
                    font-size: 0.875rem;
                    margin-bottom: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .modern-modal-footer {
                    background: white;
                    padding: 1.5rem 2rem;
                    border-top: 1px solid #e5e7eb;
                    display: flex;
                    gap: 1rem;
                    justify-content: flex-end;
                }

                .modern-btn {
                    padding: 0.75rem 1.5rem;
                    border-radius: 6px;
                    font-weight: 500;
                    font-size: 0.875rem;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    min-width: 120px;
                    justify-content: center;
                }

                .btn-secondary-modern {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                }

                .btn-secondary-modern:hover:not(:disabled) {
                    background: #e2e8f0;
                    color: #475569;
                }

                .btn-primary-modern {
                    background: #3b82f6;
                    color: white;
                }

                .btn-primary-modern:hover:not(:disabled) {
                    background: #2563eb;
                    transform: translateY(-1px);
                }

                .btn-primary-modern:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                }

                .loading-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid currentColor;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @media (max-width: 768px) {
                    .modern-modal-header,
                    .modern-modal-body,
                    .modern-modal-footer {
                        padding: 1.5rem;
                    }

                    .modern-modal-footer {
                        flex-direction: column;
                    }

                    .modern-btn {
                        width: 100%;
                    }
                }
            `}</style>

            <Modal
                className="modern-modal fade"
                size="lg"
                show={show}
                onHide={handleModalClose}
                backdrop={isUpdating ? 'static' : true}
                keyboard={!isUpdating}
            >
                <Modal.Header className="modern-modal-header">
                    <Modal.Title className="modern-modal-title">
                        <Calendar size={24} />
                        {title || 'Redigera lönekörning'}
                    </Modal.Title>
                    {!isUpdating && (
                        <button
                            className="btn-close"
                            onClick={handleModalClose}
                            aria-label="Stäng"
                        >
                            <X size={20} />
                        </button>
                    )}
                </Modal.Header>

                <Modal.Body className="modern-modal-body">
                    <div className="modal-info-section">
                        <Info size={20} className="modal-info-icon" />
                        <div className="modal-info-text">
                            <strong>Uppdatera utbetalningsdatum</strong><br />
                            Välj det datum då lönerna kommer att betalas ut till de anställda.
                            {payrollPeriod && (
                                <>
                                    <br />
                                    <strong>Viktigt:</strong> Utbetalningsdatum måste vara inom löneperioden för {' '}
                                    {new Date(payrollPeriod.year, payrollPeriod.month - 1).toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })}
                                    {' '}({new Date(payrollPeriod.year, payrollPeriod.month - 1, 1).toLocaleDateString('sv-SE')} - {' '}
                                    {new Date(payrollPeriod.year, payrollPeriod.month, 0).toLocaleDateString('sv-SE')}).
                                </>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="error-message">
                            <X size={16} />
                            {error}
                        </div>
                    )}

                    <div className="row">
                        <div className="col-md-6">
                            <div className="modern-form-group">
                                <label className="modern-form-label">
                                    Utbetalningsdatum *
                                </label>
                                <input
                                    type="date"
                                    className="modern-form-input"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    min={(() => {
                                        const today = formatDateForInput(new Date());
                                        if (payrollPeriod) {
                                            const periodStart = formatDateForInput(new Date(payrollPeriod.year, payrollPeriod.month - 1, 1));
                                            // Use the later of today or period start
                                            return today > periodStart ? today : periodStart;
                                        }
                                        return today;
                                    })()}
                                    max={payrollPeriod ? 
                                        formatDateForInput(new Date(payrollPeriod.year, payrollPeriod.month, 0))
                                        : undefined
                                    }
                                    disabled={isUpdating}
                                    required
                                />
                            </div>
                        </div>

                        <div className="col-md-6">
                            {selectedDate && (
                                <div className="date-preview">
                                    <div className="date-preview-label">Valt datum</div>
                                    <div className="date-preview-value">
                                        {getPayoutDateInfo()}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className="modern-modal-footer">
                    <button
                        className="modern-btn btn-secondary-modern"
                        onClick={handleModalClose}
                        disabled={isUpdating}
                    >
                        <X size={16} />
                        Avbryt
                    </button>

                    <button
                        className="modern-btn btn-primary-modern"
                        onClick={handleSaveClick}
                        disabled={isUpdating || !selectedDate}
                    >
                        {isUpdating ? (
                            <>
                                <div className="loading-spinner" />
                                Uppdaterar...
                            </>
                        ) : (
                            <>
                                <Save size={16} />
                                Spara ändringar
                            </>
                        )}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EditPayrollModal;

